<template>
  <div>
    <b-card>
      <TitleTable titleTable="Inventory movements" iconTable="fas fa-exchange-alt" />
      <b-row class="wl-filters m-1 mb-1 align-items-start">
        <b-col cols="12" md="4">
          <b-form-group label="Facility" label-class="font-weight-bold mb-1" class="mb-md-0">
            <treeselect
              v-model="filters.facilities_id"
              :options="facilityOptions"
              :normalizer="facilityNormalizer"
              :multiple="true"
              :clearable="true"
              placeholder="All facilities"
              :disabled="optionsLoading" />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="4">
          <b-form-group label="Type" label-class="font-weight-bold mb-1" class="mb-md-0">
            <b-form-select v-model="filters.type" :options="typeFilterOptions">
              <template #first>
                <b-form-select-option :value="null">All types</b-form-select-option>
              </template>
            </b-form-select>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="4">
          <b-form-group label="Item" label-class="font-weight-bold mb-1" class="mb-md-0">
            <treeselect
              v-model="filters.inv_item_id"
              :options="itemOptions"
              :normalizer="itemNormalizer"
              :clearable="true"
              placeholder="All items"
              :disabled="optionsLoading" />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row class="wl-filters m-1 mb-1 align-items-start">
        <b-col cols="12" md="3">
          <b-form-group label="From" label-class="font-weight-bold mb-1" class="mb-md-0">
            <b-form-input v-model="filters.date_from" type="date" />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="3">
          <b-form-group label="To" label-class="font-weight-bold mb-1" class="mb-md-0">
            <b-form-input v-model="filters.date_to" type="date" />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="6" class="d-flex justify-content-end align-items-end pb-1">
          <b-button
            variant="outline-primary"
            class="wl-filter-btn mt-2 mt-md-0"
            :disabled="optionsLoading || loading"
            @click="load(1)">
            Filter
          </b-button>
        </b-col>
      </b-row>
      <b-row class="m-1 mb-3">
        <b-col cols="12" class="d-flex justify-content-end align-items-center pt-2 border-top">
          <GoBack class="mr-2" />
          <b-button variant="primary" @click="openRegister">Register movement</b-button>
        </b-col>
      </b-row>

      <b-table
        :items="items"
        :fields="fields"
        bordered
        responsive
        show-empty
        empty-text="No movements found">
        <template #cell(type)="data">
          <b-badge :variant="typeVariant(data.value)">
            {{ typeLabel(data.value) }}
          </b-badge>
        </template>
        <template #cell(item)="data">
          {{ itemLabel(data.item) }}
        </template>
        <template #cell(facility)="data">
          {{ facilityLabel(data.item) }}
        </template>
        <template #cell(qty)="data">
          {{ formatQty(data.value) }}
        </template>
        <template #cell(notes)="data">
          <span
            v-if="data.value"
            v-b-tooltip.hover
            :title="data.value"
            class="wl-cell-truncate">
            {{ truncate(data.value, 32) }}
          </span>
          <span v-else class="text-muted font-italic">—</span>
        </template>
        <template #cell(created_at)="data">
          {{ formatDate(data.value) }}
        </template>
      </b-table>

      <PaginationTable
        :dataMeta="dataMeta"
        :totalUsers="totalUsers"
        :perPage="perPage"
        :currentPage="{ page: currentPage }"
        @page-changed="load" />
    </b-card>

    <WlFormSidebar
      :visible.sync="sidebarVisible"
      sidebar-id="inv-mov-sidebar"
      title="Register movement"
      :loading="saving"
      @submit="save"
      @close="onSidebarClose">
      <b-form-group label="Type">
        <b-form-select v-model="form.type" :options="typeFormOptions" />
      </b-form-group>
      <b-form-group label="Item">
        <treeselect
          v-model="form.inv_item_id"
          :options="itemOptions"
          :normalizer="itemNormalizer"
          placeholder="Select item"
          :disabled="optionsLoading" />
      </b-form-group>
      <b-form-group label="Facility">
        <treeselect
          v-model="form.facility_id"
          :options="facilityOptions"
          :normalizer="facilityNormalizer"
          :clearable="false"
          placeholder="Select facility"
          :disabled="optionsLoading || saving" />
      </b-form-group>
      <b-form-group v-if="form.type === 'transfer'" label="Destination facility">
        <treeselect
          v-model="form.destination_facility_id"
          :options="facilityOptions"
          :normalizer="facilityNormalizer"
          :clearable="false"
          placeholder="Select destination"
          :disabled="optionsLoading || saving" />
      </b-form-group>
      <b-form-group label="Quantity">
        <b-form-input v-model="form.qty" type="number" step="0.01" min="0.01" />
      </b-form-group>
      <b-form-group label="Notes">
        <b-form-textarea v-model="form.notes" rows="2" placeholder="Optional" />
      </b-form-group>
    </WlFormSidebar>

    <ToastNotification ref="toast" />
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BTable,
  BButton,
  BBadge,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormSelectOption,
  BFormTextarea,
  VBTooltip,
} from 'bootstrap-vue'
import Treeselect from '@riophae/vue-treeselect'
import TitleTable from '@/components/TitleTable.vue'
import GoBack from '@/components/ButtonBack.vue'
import PaginationTable from '@/components/PaginationTable'
import ToastNotification from '@/components/ToastNotification'
import WlFormSidebar from '../components/WlFormSidebar.vue'
import invMovementsApi from '@/core/services/api/weightloss-inventory/movements'
import invItemsApi from '@/core/services/api/weightloss-inventory/items'
import wlAdminMixin from '../mixins/wlAdminMixin'
import { resolveNumericFacilityId } from '@/core/services/api/weightloss-inventory/facility'
import axiosF from '@/core/services/api/admin/facilities'

const MOVEMENT_FILTER_TYPES = [
  { value: 'in', text: 'In' },
  { value: 'out', text: 'Out' },
  { value: 'adjustment_in', text: 'Adjustment in' },
  { value: 'adjustment_out', text: 'Adjustment out' },
  { value: 'transfer_in', text: 'Transfer in' },
  { value: 'transfer_out', text: 'Transfer out' },
]

const MOVEMENT_FORM_TYPES = [
  { value: 'in', text: 'In' },
  { value: 'out', text: 'Out' },
  { value: 'adjustment_in', text: 'Adjustment in' },
  { value: 'adjustment_out', text: 'Adjustment out' },
  { value: 'transfer', text: 'Transfer between facilities' },
]

function defaultForm() {
  const defaultFacility = resolveNumericFacilityId()
  return {
    type: 'in',
    inv_item_id: null,
    facility_id: defaultFacility ? Number(defaultFacility) : null,
    destination_facility_id: null,
    qty: 1,
    notes: '',
  }
}

export default {
  name: 'TableInvMovements',
  mixins: [wlAdminMixin],
  directives: { 'b-tooltip': VBTooltip },
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BButton,
    BBadge,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormSelectOption,
    BFormTextarea,
    Treeselect,
    TitleTable,
    GoBack,
    PaginationTable,
    ToastNotification,
    WlFormSidebar,
  },
  data() {
    return {
      optionsLoading: false,
      typeFormOptions: MOVEMENT_FORM_TYPES,
      typeFilterOptions: MOVEMENT_FILTER_TYPES,
      itemOptions: [],
      facilityOptions: [],
      facilityNormalizer(node) {
        return { id: node.id, label: node.name }
      },
      itemNormalizer(node) {
        return { id: node.id, label: node.label || node.name }
      },
      fields: [
        { key: 'id', label: 'ID', thStyle: { width: '4%' } },
        { key: 'type', label: 'Type' },
        { key: 'item', label: 'Item' },
        { key: 'facility', label: 'Facility' },
        { key: 'qty', label: 'Qty' },
        { key: 'notes', label: 'Notes' },
        { key: 'created_at', label: 'Date' },
      ],
      filters: {
        facilities_id: [],
        type: null,
        inv_item_id: null,
        date_from: '',
        date_to: '',
      },
      sidebarVisible: false,
      saving: false,
      form: defaultForm(),
    }
  },
  mounted() {
    this.init()
  },
  methods: {
    async init() {
      this.optionsLoading = true
      try {
        const [facilitiesRes, itemsRes] = await Promise.all([
          axiosF.facilityList(1000),
          invItemsApi.listPaginated(500, {}, 1),
        ])
        this.facilityOptions = (facilitiesRes.data || []).map(f => ({
          id: f.id,
          name: f.name,
        }))
        this.itemOptions = itemsRes.items.map(i => ({
          id: i.id,
          label: i.sku ? `${i.name} (${i.sku})` : i.name,
        }))
      } catch (e) {
        this.$refs.toast.danger(e.message || 'Could not load filter options')
      } finally {
        this.optionsLoading = false
      }
      this.load(1)
    },
    buildListParams() {
      const params = {}
      if (this.filters.facilities_id && this.filters.facilities_id.length) {
        params.facilities_id = this.filters.facilities_id
          .map(v => Number(v))
          .filter(v => Number.isFinite(v))
      }
      if (this.filters.type) params.type = this.filters.type
      if (this.filters.inv_item_id != null && this.filters.inv_item_id !== '') {
        params.inv_item_id = Number(this.filters.inv_item_id)
      }
      if (this.filters.date_from) params.date_from = this.filters.date_from
      if (this.filters.date_to) params.date_to = this.filters.date_to
      return params
    },
    typeLabel(value) {
      const t = MOVEMENT_FILTER_TYPES.find(x => x.value === value)
        || MOVEMENT_FORM_TYPES.find(x => x.value === value)
      return t ? t.text : value || '—'
    },
    typeVariant(type) {
      if (!type) return 'secondary'
      if (type === 'in' || type === 'adjustment_in' || type === 'transfer_in') return 'success'
      if (type === 'out' || type === 'adjustment_out' || type === 'transfer_out') return 'danger'
      return 'info'
    },
    itemLabel(item) {
      if (item && item.item && item.item.name) {
        const sku = item.item.sku
        return sku ? `${item.item.name} (${sku})` : item.item.name
      }
      const id = item && item.inv_item_id
      if (id != null) {
        const opt = this.itemOptions.find(x => x.id === id || x.id === Number(id))
        return opt ? opt.label : `#${id}`
      }
      return '—'
    },
    facilityLabel(item) {
      if (item && item.facility && item.facility.name) return item.facility.name
      const id = item && item.facility_id
      if (id != null) {
        const f = this.facilityOptions.find(x => x.id === id || x.id === Number(id))
        return f ? f.name : `#${id}`
      }
      return '—'
    },
    formatQty(value) {
      if (value === null || value === undefined || value === '') return '—'
      const n = Number(value)
      return Number.isNaN(n) ? value : n
    },
    formatDate(value) {
      if (!value) return '—'
      return String(value).slice(0, 16).replace('T', ' ')
    },
    truncate(text, max) {
      const s = String(text)
      return s.length > max ? `${s.slice(0, max)}...` : s
    },
    onSidebarClose() {
      this.form = defaultForm()
    },
    openRegister() {
      this.form = defaultForm()
      this.sidebarVisible = true
    },
    async load(page = 1) {
      this.loading = true
      try {
        const res = await invMovementsApi.listPaginated(
          this.perPage,
          this.buildListParams(),
          page
        )
        this.applyPagination(res)
      } catch (e) {
        this.$refs.toast.danger(e.message)
      } finally {
        this.loading = false
      }
    },
    async save() {
      if (!this.form.type) {
        this.$refs.toast.danger('Type is required')
        return
      }
      if (!this.form.inv_item_id) {
        this.$refs.toast.danger('Item is required')
        return
      }
      if (this.form.facility_id == null || this.form.facility_id === '') {
        this.$refs.toast.danger('Facility is required')
        return
      }
      if (this.form.type === 'transfer') {
        if (
          this.form.destination_facility_id == null
          || this.form.destination_facility_id === ''
        ) {
          this.$refs.toast.danger('Destination facility is required for transfers')
          return
        }
        if (Number(this.form.destination_facility_id) === Number(this.form.facility_id)) {
          this.$refs.toast.danger('Origin and destination must be different')
          return
        }
      }
      const qty = Number(this.form.qty)
      if (!qty || qty <= 0) {
        this.$refs.toast.danger('Quantity must be greater than zero')
        return
      }

      this.saving = true
      try {
        const payload = {
          type: this.form.type,
          inv_item_id: Number(this.form.inv_item_id),
          facility_id: Number(this.form.facility_id),
          qty,
        }
        const notes = String(this.form.notes || '').trim()
        if (notes) payload.notes = notes
        if (this.form.type === 'transfer') {
          payload.destination_facility_id = Number(this.form.destination_facility_id)
        }

        await invMovementsApi.create(payload)
        this.$refs.toast.success('Movement recorded')
        this.sidebarVisible = false
        this.form = defaultForm()
        this.load(1)
      } catch (e) {
        this.handleApiError(e, this.$refs.toast)
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.wl-filter-btn {
  min-width: 7rem;
}

.wl-cell-truncate {
  display: inline-block;
  max-width: 10rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
}
</style>
